import { Host, h } from "@stencil/core";
import { generateRandomId } from "../../utilities/stringUtilities";
export class DnnSelect {
    constructor() {
        /** Defines the type of automatic completion the browser can use.
         * See https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
         */
        this.autocomplete = "off";
        /** The value of the input. */
        this.value = "";
        this.focused = false;
        this.valid = true;
    }
    /** Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState */
    async checkValidity() {
        var validity = this.select.checkValidity();
        if (!validity) {
            this.fieldset.setValidity(false, this.select.validationMessage);
        }
        return this.select.validity;
    }
    componentWillLoad() {
        this.labelId = generateRandomId();
        this.observer = new MutationObserver((mutations) => {
            for (let mutation of mutations) {
                if (mutation.type === 'childList') {
                    this.applySlottedItemsToSelect();
                }
            }
        });
        const config = { attributes: true, childList: true, subtree: true };
        this.observer.observe(this.el, config);
    }
    componentDidLoad() {
        requestAnimationFrame(() => {
            this.applySlottedItemsToSelect();
            // Initialize value based on the "selected" attribute of slotted options
            const slottedItems = this.el.querySelectorAll('option');
            const selectedOption = Array.from(slottedItems).find(option => option.hasAttribute('selected'));
            if (selectedOption) {
                this.value = selectedOption.getAttribute('value') || selectedOption.textContent || '';
            }
            else if (slottedItems.length > 0) {
                this.value = slottedItems[0].getAttribute('value') || slottedItems[0].textContent || '';
            }
            // Set the original value for form reset
            this.originalValue = this.value;
            var validity = this.select.validity;
            var validityMessage = validity.valid ? "" : this.select.validationMessage;
            this.internals.setValidity(this.select.validity, validityMessage);
            this.setFormValue();
        });
    }
    formResetCallback() {
        this.internals.setValidity({});
        this.value = this.originalValue;
        this.internals.setFormValue("");
        this.select.selectedIndex = -1;
    }
    applySlottedItemsToSelect() {
        var _a;
        const slottedItems = (_a = this.slot) === null || _a === void 0 ? void 0 : _a.assignedElements();
        slottedItems === null || slottedItems === void 0 ? void 0 : slottedItems.forEach((item) => {
            if (item.nodeName === "OPTION") {
                const optionElement = item;
                this.select.appendChild(optionElement);
            }
        });
    }
    setFormValue() {
        if (this.name != undefined) {
            var data = new FormData();
            data.append(this.name, this.value);
            this.internals.setFormValue(data);
        }
    }
    handleChange(value) {
        this.value = value;
        var valid = this.select.checkValidity();
        this.valid = valid;
        this.valueChange.emit(this.value);
        this.setFormValue();
        if (this.valid) {
            this.internals.setValidity({});
            this.fieldset.setValidity(true);
        }
        else {
            this.internals.setValidity({ customError: true }, this.select.validationMessage);
            this.fieldset.setValidity(false, this.select.validationMessage);
        }
    }
    handleInvalid() {
        this.valid = false;
        if (this.customValidityMessage == undefined) {
            this.customValidityMessage = this.select.validationMessage;
        }
    }
    handleBlur() {
        this.focused = false;
        var validity = this.select.checkValidity();
        this.valid = validity;
        this.fieldset.setValidity(validity, this.select.validationMessage);
        this.internals.setValidity(this.select.validity, this.select.validationMessage);
    }
    render() {
        var _a;
        return (h(Host, { key: '7d866084b937eae7e2955191b8f36e2368d34504', tabIndex: this.focused ? -1 : 0, onFocus: () => this.select.focus(), onBlur: () => this.select.blur() }, h("dnn-fieldset", { key: '91733c7d9a5d93d4572a3ae6551aa3bd1c6cf810', invalid: !this.valid, focused: this.focused, label: `${(_a = this.label) !== null && _a !== void 0 ? _a : ""}${this.required ? " *" : ""}`, helpText: this.helpText, id: this.labelId, onClick: () => !this.focused && this.select.focus(), ref: el => this.fieldset = el }, h("div", { key: '7bc975f019c8719f2ea7416e43cd74178b76ca8a', class: "inner-container" }, h("select", { key: '06496b37f53cd023ee4f07f6e7ff0407a04c2cb3', ref: el => this.select = el, autoComplete: this.autocomplete, onFocus: () => this.focused = true, onBlur: () => this.handleBlur(), onChange: e => this.handleChange(e.target.value), onInvalid: () => this.handleInvalid(), required: this.required, disabled: this.disabled, "aria-labelledby": this.labelId }, h("slot", { key: '73ea19ab4810f358f669278492d4074078615430', ref: el => this.slot = el })), !this.valid &&
            h("svg", { key: 'bd07cf76878c6f896928dfa56aec988424123288', xmlns: "http://www.w3.org/2000/svg", viewBox: "0 -960 960 960", class: "error" }, h("path", { key: '78f9bd0be6482c85446a46d7480dca8bd56d1dec', d: "M479.982-280q14.018 0 23.518-9.482 9.5-9.483 9.5-23.5 0-14.018-9.482-23.518-9.483-9.5-23.5-9.5-14.018 0-23.518 9.482-9.5 9.483-9.5 23.5 0 14.018 9.482 23.518 9.483 9.5 23.5 9.5ZM453-433h60v-253h-60v253Zm27.266 353q-82.734 0-155.5-31.5t-127.266-86q-54.5-54.5-86-127.341Q80-397.681 80-480.5q0-82.819 31.5-155.659Q143-709 197.5-763t127.341-85.5Q397.681-880 480.5-880q82.819 0 155.659 31.5Q709-817 763-763t85.5 127Q880-563 880-480.266q0 82.734-31.5 155.5T763-197.684q-54 54.316-127 86Q563-80 480.266-80Zm.234-60Q622-140 721-239.5t99-241Q820-622 721.188-721 622.375-820 480-820q-141 0-240.5 98.812Q140-622.375 140-480q0 141 99.5 240.5t241 99.5Zm-.5-340Z" }))))));
    }
    static get is() { return "dnn-select"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["dnn-select.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["dnn-select.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "attribute": "label",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The label for this input."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "name": {
                "type": "string",
                "attribute": "name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The name for this input, if used in forms."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "required": {
                "type": "boolean",
                "attribute": "required",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines whether the field requires having a value."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "helpText": {
                "type": "string",
                "attribute": "help-text",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines the help label displayed under the field."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "disabled": {
                "type": "boolean",
                "attribute": "disabled",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean | undefined",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Defines whether the field is disabled."
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "autocomplete": {
                "type": "string",
                "attribute": "autocomplete",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines the type of automatic completion the browser can use.\nSee https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "\"off\""
            },
            "value": {
                "type": "string",
                "attribute": "value",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The value of the input."
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "defaultValue": "\"\""
            }
        };
    }
    static get states() {
        return {
            "focused": {},
            "valid": {},
            "customValidityMessage": {}
        };
    }
    static get events() {
        return [{
                "method": "valueChange",
                "name": "valueChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fires when the value has changed and the user exits the input."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "checkValidity": {
                "complexType": {
                    "signature": "() => Promise<ValidityState>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "ValidityState": {
                            "location": "global",
                            "id": "global::ValidityState"
                        }
                    },
                    "return": "Promise<ValidityState>"
                },
                "docs": {
                    "text": "Reports the input validity details. See https://developer.mozilla.org/en-US/docs/Web/API/ValidityState",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get attachInternalsMemberName() { return "internals"; }
}
//# sourceMappingURL=dnn-select.js.map
